// Calculate grade between two points
export const calculateGrade = (point1, point2) => {
  if (!point1 || !point2) return 0;
  
  const elevationChange = point2.elevation - point1.elevation;
  const distance = Math.sqrt(
    Math.pow(point2.latitude - point1.latitude, 2) +
    Math.pow(point2.longitude - point1.longitude, 2)
  ) * 111319.9; // Convert to meters

  if (distance === 0) return 0;
  return ((elevationChange / distance) * 100).toFixed(1);
};

// Smooth profile data using moving average
export const smoothProfile = (points, windowSize = 5) => {
  if (points.length < windowSize) return points;

  const smoothed = [];
  for (let i = 0; i < points.length; i++) {
    let sum = 0;
    let count = 0;
    
    for (let j = Math.max(0, i - Math.floor(windowSize/2)); 
         j < Math.min(points.length, i + Math.floor(windowSize/2) + 1); 
         j++) {
      sum += points[j].elevation;
      count++;
    }
    
    smoothed.push({
      ...points[i],
      elevation: sum / count
    });
  }
  
  return smoothed;
};

// Detect curves using three-point method
export const detectCurves = (points, threshold = 0.1, convoySpecs = null) => {
  const curves = [];
  if (points.length < 3) return curves;

  // Calculate minimum turning radius based on convoy specs
  let minTurningRadius = null;
  if (convoySpecs) {
    // R = L²/2w where L is vehicle length and w is width
    minTurningRadius = Math.pow(convoySpecs.length, 2) / (2 * convoySpecs.width);
  }

  for (let i = 1; i < points.length - 1; i++) {
    const p1 = points[i-1];
    const p2 = points[i];
    const p3 = points[i+1];

    // Calculate vectors
    const v1 = {
      x: p2.longitude - p1.longitude,
      y: p2.latitude - p1.latitude
    };
    const v2 = {
      x: p3.longitude - p2.longitude,
      y: p3.latitude - p2.latitude
    };

    // Calculate angle between vectors
    const dot = v1.x * v2.x + v1.y * v2.y;
    const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
    const angle = Math.acos(dot / (mag1 * mag2));

    // If angle is significant, we have a curve
    if (Math.abs(angle) > threshold) {
      const radius = calculateCurveRadius(p1, p2, p3);
      const isCritical = minTurningRadius && radius < minTurningRadius;
      
      curves.push({
        startIndex: i-1,
        midIndex: i,
        endIndex: i+1,
        angle: angle * (180 / Math.PI),
        radius,
        center: calculateCurveCenter(p1, p2, p3),
        type: angle > Math.PI/2 ? 'sharp' : 'gradual',
        isCritical,
        minRadius: minTurningRadius,
        details: {
          angle: angle * (180 / Math.PI),
          radius,
          minRadius: minTurningRadius,
          difference: minTurningRadius ? (radius - minTurningRadius) : null,
          percentageOfMin: minTurningRadius ? (radius / minTurningRadius * 100) : null
        }
      });
    }
  }

  return curves;
};

// Calculate radius of curvature using three points
const calculateCurveRadius = (p1, p2, p3) => {
  const a = Math.sqrt(
    Math.pow(p2.longitude - p1.longitude, 2) + 
    Math.pow(p2.latitude - p1.latitude, 2)
  );
  const b = Math.sqrt(
    Math.pow(p3.longitude - p2.longitude, 2) + 
    Math.pow(p3.latitude - p2.latitude, 2)
  );
  const c = Math.sqrt(
    Math.pow(p3.longitude - p1.longitude, 2) + 
    Math.pow(p3.latitude - p1.latitude, 2)
  );

  // Semiperimeter
  const s = (a + b + c) / 2;
  
  // Area using Heron's formula
  const area = Math.sqrt(s * (s - a) * (s - b) * (s - c));
  
  // Radius = abc/(4A)
  return (a * b * c) / (4 * area);
};

// Calculate center point of curve
const calculateCurveCenter = (p1, p2, p3) => {
  const d = 2 * (p1.latitude * (p2.longitude - p3.longitude) + 
                 p2.latitude * (p3.longitude - p1.longitude) + 
                 p3.latitude * (p1.longitude - p2.longitude));

  const centerLat = ((p1.latitude * p1.latitude + p1.longitude * p1.longitude) * (p2.longitude - p3.longitude) +
                    (p2.latitude * p2.latitude + p2.longitude * p2.longitude) * (p3.longitude - p1.longitude) +
                    (p3.latitude * p3.latitude + p3.longitude * p3.longitude) * (p1.longitude - p2.longitude)) / d;

  const centerLng = ((p1.latitude * p1.latitude + p1.longitude * p1.longitude) * (p3.latitude - p2.latitude) +
                    (p2.latitude * p2.latitude + p2.longitude * p2.longitude) * (p1.latitude - p3.latitude) +
                    (p3.latitude * p3.latitude + p3.longitude * p3.longitude) * (p2.latitude - p1.latitude)) / d;

  return { latitude: centerLat, longitude: centerLng };
};